import React, { useEffect, useState } from 'react';
import Artist from '../models/Artist';
import User from '../models/User';
import firebase from '../firebaseConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faPlus } from '@fortawesome/free-solid-svg-icons';
import ArtistRow from './ArtistRow';
import ArtistInviteComposer from './ArtistInviteComposer';
import ManageArtistAccess from './ManageArtistAccess';

const byName = (a, b) => a.name.localeCompare(b.name);

const SelectArtistPopup = ({ onSelect, onClose, currentArtist, onArtistDeparted }) => {
    const [artists, setArtists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [view, setView] = useState('list'); // list | invite | manageLinks
    const [activeArtistId, setActiveArtistId] = useState(null);
    const [openPanelId, setOpenPanelId] = useState(null);

    const [projectCounts, setProjectCounts] = useState({});
    const [memberUids, setMemberUids] = useState({});
    const [profiles, setProfiles] = useState({});
    const [pendingCounts, setPendingCounts] = useState({});
    const [busyId, setBusyId] = useState(null);
    const [rowErrors, setRowErrors] = useState({});

    const [addMode, setAddMode] = useState(false);
    const [addDraft, setAddDraft] = useState('');
    const [addBusy, setAddBusy] = useState(false);
    const [addError, setAddError] = useState('');

    const currentUid = firebase.auth().currentUser?.uid;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (!currentUid) throw new Error('Not signed in.');
                const snap = await firebase.database().ref('users').child(currentUid).child('artists').once('value');
                const ids = Object.keys(snap.val() || {});
                const resolved = await Promise.all(ids.map((id) => Artist.getArtistById(id)));
                if (cancelled) return;
                resolved.sort(byName);
                setArtists(resolved);
                setLoading(false);
                // Project counts trail the list — picker renders without them.
                resolved.forEach((a) => {
                    a.getProjectCount()
                        .then((count) => {
                            if (!cancelled) setProjectCounts((prev) => ({ ...prev, [a.id]: count }));
                        })
                        .catch(() => {});
                });
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || 'Could not load artists.');
                    setLoading(false);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [currentUid]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Escape' || view !== 'list') return;
            if (addMode) { setAddMode(false); setAddError(''); }
            else onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, view, addMode]);

    // Resolve member uids to display names as panels reveal them.
    useEffect(() => {
        const uids = new Set();
        Object.values(memberUids).forEach((list) => (list || []).forEach((u) => uids.add(u)));
        let cancelled = false;
        uids.forEach((uid) => {
            if (profiles[uid] !== undefined) return;
            User.getUserName(uid)
                .then((name) => {
                    if (!cancelled) setProfiles((prev) => ({ ...prev, [uid]: name || null }));
                })
                .catch(() => {
                    if (!cancelled) setProfiles((prev) => ({ ...prev, [uid]: null }));
                });
        });
        return () => { cancelled = true; };
    }, [memberUids]); // eslint-disable-line react-hooks/exhaustive-deps

    const setRowError = (id, msg) => {
        setRowErrors((prev) => ({ ...prev, [id]: msg || undefined }));
    };

    const loadPendingCount = async (artist) => {
        try {
            const snap = await firebase.database()
                .ref('artists').child(artist.id).child('pending invites').once('value');
            setPendingCounts((prev) => ({ ...prev, [artist.id]: snap.numChildren() }));
        } catch { /* members-only read; ignore */ }
    };

    const handleTogglePanel = (artist) => {
        const opening = openPanelId !== artist.id;
        setOpenPanelId(opening ? artist.id : null);
        if (opening) {
            if (memberUids[artist.id] === undefined) {
                setMemberUids((prev) => ({ ...prev, [artist.id]: artist.members }));
            }
            loadPendingCount(artist);
        }
    };

    const handleRename = async (artist, name) => {
        try {
            await artist.rename(name);
            setRowError(artist.id, '');
            setArtists((prev) => [...prev].sort(byName));
        } catch (err) {
            setRowError(artist.id, err?.message || "Couldn't rename artist.");
            throw err;
        }
    };

    const departArtist = (artist) => {
        const remaining = artists.filter((a) => a.id !== artist.id);
        setArtists(remaining);
        if (openPanelId === artist.id) setOpenPanelId(null);
        if (currentArtist?.id === artist.id) onArtistDeparted?.(remaining[0] || null);
    };

    const handleLeave = async (artist) => {
        if (!window.confirm(`Leave ${artist.name}? You'll lose access to its projects.`)) return;
        setBusyId(artist.id);
        setRowError(artist.id, '');
        try {
            await artist.removeMember(currentUid);
            departArtist(artist);
        } catch (err) {
            setRowError(artist.id, err?.message || "Couldn't leave artist.");
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (artist) => {
        setBusyId(artist.id);
        setRowError(artist.id, '');
        try {
            await artist.delete();
            departArtist(artist);
        } catch (err) {
            setRowError(artist.id, err?.message || "Couldn't delete artist.");
            throw err;
        } finally {
            setBusyId(null);
        }
    };

    const handleRemoveMember = async (artist, uid) => {
        const who = profiles[uid] || 'this member';
        if (!window.confirm(`Remove ${who} from ${artist.name}?`)) return;
        setBusyId(artist.id);
        setRowError(artist.id, '');
        try {
            await artist.removeMember(uid);
            await artist.refreshMembers();
            setMemberUids((prev) => ({ ...prev, [artist.id]: artist.members }));
        } catch (err) {
            setRowError(artist.id, err?.message || "Couldn't remove member.");
        } finally {
            setBusyId(null);
        }
    };

    const handleAddArtist = async () => {
        const name = addDraft.trim();
        if (!name || addBusy) return;
        setAddBusy(true);
        setAddError('');
        try {
            const created = await Artist.create(name);
            onSelect(created);
        } catch (err) {
            setAddError(err?.message || "Couldn't create artist.");
            setAddBusy(false);
        }
    };

    const activeArtist = artists.find((a) => a.id === activeArtistId) || null;

    if (view === 'invite' && activeArtist) {
        return (
            <ArtistInviteComposer
                artist={activeArtist}
                onBack={() => setView('list')}
                onClose={onClose}
            />
        );
    }
    if (view === 'manageLinks' && activeArtist) {
        return (
            <ManageArtistAccess
                artist={activeArtist}
                onBack={() => { setView('list'); loadPendingCount(activeArtist); }}
                onClose={onClose}
            />
        );
    }

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div
                className="modal-card artist-picker-card"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Change Artist"
            >
                <div className="modal-head">
                    <div className="modal-head-text">
                        <div className="modal-eyebrow">SWITCH ARTIST</div>
                        <h2 className="modal-title">Pick a workspace</h2>
                    </div>
                    <button className="modal-close" onClick={onClose} aria-label="Close">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <div className="artist-picker-list">
                    {loading && (
                        <>
                            <div className="artist-row skeleton-row">
                                <div className="skeleton-avatar" />
                                <div className="skeleton-line" style={{ width: '50%' }} />
                            </div>
                            <div className="artist-row skeleton-row">
                                <div className="skeleton-avatar" />
                                <div className="skeleton-line" style={{ width: '70%' }} />
                            </div>
                        </>
                    )}
                    {!loading && error && <div className="artist-empty">{error}</div>}
                    {!loading && !error && artists.length === 0 && (
                        <div className="artist-empty">You don't belong to any artists yet.</div>
                    )}
                    {!loading && !error && artists.map((artist) => (
                        <ArtistRow
                            key={artist.id}
                            artist={artist}
                            isCurrent={currentArtist?.id === artist.id}
                            currentUid={currentUid}
                            projectCount={projectCounts[artist.id]}
                            memberCount={memberUids[artist.id]?.length ?? artist.members.length}
                            panelOpen={openPanelId === artist.id}
                            busy={busyId === artist.id}
                            rowError={rowErrors[artist.id]}
                            memberUids={memberUids[artist.id]}
                            profiles={profiles}
                            pendingCount={pendingCounts[artist.id] || 0}
                            onSelect={() => onSelect(artist)}
                            onRename={(name) => handleRename(artist, name)}
                            onTogglePanel={() => handleTogglePanel(artist)}
                            onLeave={() => handleLeave(artist)}
                            onDelete={() => handleDelete(artist)}
                            onRemoveMember={(uid) => handleRemoveMember(artist, uid)}
                            onInvite={() => { setActiveArtistId(artist.id); setView('invite'); }}
                            onManageLinks={() => { setActiveArtistId(artist.id); setView('manageLinks'); }}
                        />
                    ))}

                    {!loading && !error && (
                        addMode ? (
                            <div className="add-artist-row is-editing">
                                <input
                                    className="new-project-input"
                                    autoFocus
                                    value={addDraft}
                                    disabled={addBusy}
                                    placeholder="New artist name"
                                    onChange={(e) => setAddDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') { e.preventDefault(); handleAddArtist(); }
                                        else if (e.key === 'Escape') { e.preventDefault(); setAddMode(false); setAddError(''); }
                                    }}
                                />
                                <div className="new-project-actions">
                                    <button
                                        className="btn-ghost"
                                        disabled={addBusy}
                                        onClick={() => { setAddMode(false); setAddError(''); }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn-primary"
                                        disabled={addBusy || !addDraft.trim()}
                                        onClick={handleAddArtist}
                                    >
                                        {addBusy ? 'Creating…' : 'Create'}
                                    </button>
                                </div>
                                {addError && <div className="new-project-error">{addError}</div>}
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="add-artist-row"
                                onClick={() => { setAddDraft(''); setAddError(''); setAddMode(true); }}
                            >
                                <span className="add-artist-plus">
                                    <FontAwesomeIcon icon={faPlus} />
                                </span>
                                <span>Add artist</span>
                            </button>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default SelectArtistPopup;
